import { useQuery } from '@tanstack/react-query'
import { createFileRoute, getRouteApi, redirect } from '@tanstack/react-router'
import { z } from 'zod'

import { AdminUserDetailPage } from '@/features/admin-users'
// Through `queries`, not the feature index: a loader is eager by construction.
import {
  adminUserQuery,
  adminUserSessionsQuery,
  userAuditQuery,
} from '@/features/admin-users/queries'
import { PERMISSION_ADMIN } from '@/features/auth'
import { campaignCountQuery } from '@/features/campaigns/queries'

import { can, sessionQuery } from '@/app/router/session'

const searchSchema = z.object({
  auditPage: z.number().int().min(0).default(0),
})

export const Route = createFileRoute('/_auth/admin/users/$userId')({
  beforeLoad: ({ context }) => {
    if (!can(context, PERMISSION_ADMIN)) throw redirect({ to: '/' })
  },
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ auditPage: search.auditPage }),
  /*
   * All three, and the two that were left out were left out for the same bad
   * reason: "secondary panels reserve their own space". Both are `DataTable`s,
   * and a table's pending state is eight skeleton rows whose count nothing can
   * know — reserving is exactly what they cannot do. Measured on this page: the
   * column stood at 1598px for 6ms between the 373px list and the 1084px
   * record, and after only the audit log was added, at 1341px for 11ms.
   *
   * Every exemption here was reasoned and wrong. What settled it was the frame
   * trace, each time.
   */
  loader: async ({ context, params, deps }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(adminUserQuery(params.userId)),
      context.queryClient.ensureQueryData(userAuditQuery(params.userId, deps.auditPage)),
      context.queryClient.ensureQueryData(adminUserSessionsQuery(params.userId)),
      context.queryClient.ensureQueryData(campaignCountQuery(params.userId)),
    ])
  },
  component: AdminUserDetailRoute,
})

const routeApi = getRouteApi('/_auth/admin/users/$userId')

function AdminUserDetailRoute() {
  const { userId } = routeApi.useParams()
  const { auditPage } = routeApi.useSearch()
  const navigate = routeApi.useNavigate()
  const { data: session } = useQuery(sessionQuery())

  return (
    <AdminUserDetailPage
      userId={userId}
      currentUserId={session?.id ?? ''}
      auditPage={auditPage}
      onAuditPageChange={(next) =>
        void navigate({ search: (prev) => ({ ...prev, auditPage: next }) })
      }
    />
  )
}
